import { useCallback, useEffect, useMemo, useState } from 'react';
import { useGeolocated } from 'react-geolocated';

const DISMISS_STORAGE_KEY = 'cys.geo.permissionDismissals';
const DISMISS_LIMIT = 3;
const POSITION_TIMEOUT_MS = 10000;
const POSITION_MAX_AGE_MS = 60000;

const POSITION_OPTIONS: PositionOptions = {
  enableHighAccuracy: false,
  timeout: POSITION_TIMEOUT_MS,
  maximumAge: POSITION_MAX_AGE_MS,
};

export const NEAR_ME_STATUS = {
  Idle: 'idle',
  Asking: 'asking',
  Granted: 'granted',
  Denied: 'denied',
  Unavailable: 'unavailable',
} as const;

export type NearMeStatus = (typeof NEAR_ME_STATUS)[keyof typeof NEAR_ME_STATUS];

const readDismissCount = (): number => {
  if (typeof window === 'undefined') return 0;
  return Number(window.localStorage.getItem(DISMISS_STORAGE_KEY) || '0');
};

const writeDismissCount = (n: number): void => {
  if (typeof window === 'undefined') return;
  window.localStorage.setItem(DISMISS_STORAGE_KEY, String(n));
};

/**
 * Hook for the SearchPage "near me" filter: wraps `react-geolocated` with
 * a state machine + dismissal-count persistence so the FE can pre-prompt
 * the user once or twice and then back off.
 *
 * @returns
 *   `status`: idle / asking / granted / denied / unavailable
 *   `coords`: `{ latitude, longitude }` once granted
 *   `request()`: trigger the native browser permission prompt
 *   `dismiss()`: record that the user skipped the pre-prompt
 *   `isPersistentlyDeclined`: true once they've dismissed `DISMISS_LIMIT` times
 */
export const useNearMe = () => {
  const {
    coords,
    isGeolocationAvailable,
    getPosition,
    positionError,
  } = useGeolocated({
    positionOptions: POSITION_OPTIONS,
    userDecisionTimeout: null,
    suppressLocationOnMount: true,
  });

  const [status, setStatus] = useState<NearMeStatus>(NEAR_ME_STATUS.Idle);

  useEffect(() => {
    if (!isGeolocationAvailable) setStatus(NEAR_ME_STATUS.Unavailable);
    else if (coords) setStatus(NEAR_ME_STATUS.Granted);
    else if (positionError) setStatus(NEAR_ME_STATUS.Denied);
  }, [isGeolocationAvailable, positionError, coords]);

  const request = useCallback(() => {
    setStatus(NEAR_ME_STATUS.Asking);
    getPosition();
  }, [getPosition]);

  const dismiss = useCallback(() => {
    writeDismissCount(readDismissCount() + 1);
  }, []);

  const stableCoords = useMemo(
    () => (coords ? { latitude: coords.latitude, longitude: coords.longitude } : null),
    [coords],
  );

  return {
    status,
    coords: stableCoords,
    request,
    dismiss,
    isPersistentlyDeclined: readDismissCount() >= DISMISS_LIMIT,
  };
};
